export type Column = {
  attrInternalName: string;
  attrDataType: string;
  valueOptions?: { label: string; value: string }[];
  valueLabelPairs?: { label: string; value: string }[];
};

export interface ColumnType {
  name: string;
  type: string;
}

export const buildSearchQuery = (
  query: string,
  allAccessibleColumns: Column[],
  columnTypes: ColumnType[]
): string => {
  const normalizedQuery = query.trim().toLowerCase();

  const stringDataTypes = ['text', 'varchar', 'char', 'citext', 'string', 'textarea', 'email'];
  const dateDataTypes = ['date', 'timestamp', 'datetime'];
  const numberDataTypes = ['number'];

  const graphqlStringLiteral = (value: any): string => JSON.stringify(String(value));

  const escapeILikeForGraphQL = (value: string): string =>
    value
      .replace(/\\/g, '\\\\\\\\')
      .replace(/%/g, '\\\\%')
      .replace(/_/g, '\\\\_')
      .replace(/"/g, '\\"');

  const graphqlILikeLiteral = (value: string): string =>
    graphqlStringLiteral(`%${escapeILikeForGraphQL(value)}%`);



  const dropdownMatches: { column: string; value: string }[] = [];
  allAccessibleColumns.forEach((col) => {
    const options = col.valueOptions || col.valueLabelPairs;
    if (options && Array.isArray(options)) {
      const matches = options.filter((opt) =>
        opt.label?.toLowerCase().includes(normalizedQuery)
      );
      matches.forEach((match) => {
        dropdownMatches.push({
          column: col.attrInternalName,
          value: graphqlStringLiteral(match.value),
        });
      });
    }
  });

  const dropdownFilters: string[] = [];
  const dropdownMatchMap: Record<string, string[]> = {};
  dropdownMatches.forEach(({ column, value }) => {
    if (!dropdownMatchMap[column]) dropdownMatchMap[column] = [];
    dropdownMatchMap[column].push(value);
  });

  for (const [column, values] of Object.entries(dropdownMatchMap)) {
    dropdownFilters.push(`{ ${column}: { _in: [${values.join(', ')}] } }`);
  }

  const parseDDMMYYYY = (input: string): string | null => {
    const match = input.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (!match) return null;
    const [, dd, mm, yyyy] = match;
    const isoDate = `${yyyy}-${mm}-${dd}`;
    const parsed = new Date(isoDate);
    return isNaN(parsed.getTime()) ? null : parsed.toISOString().split('T')[0];
  };

  const parsedQueryDate = parseDDMMYYYY(query);
  const isValidDate = !!parsedQueryDate;
  const parsedQueryNumber = Number(query);
  const isValidNumber = !isNaN(parsedQueryNumber);

  let filters: string[] = [];

  if (isValidDate) {
    const dateCols = allAccessibleColumns.filter((col) =>
      dateDataTypes.includes(col.attrDataType?.toLowerCase())
    );
    const stringCols = allAccessibleColumns.filter((col) =>
      stringDataTypes.includes(col.attrDataType?.toLowerCase())
    );

    filters = [
      ...stringCols.map((col) =>
        `{ ${col.attrInternalName}: { _ilike: ${graphqlILikeLiteral(query)} } }`,
      ),
      ...dateCols.map(
        (col) => `{ ${col.attrInternalName}: { _eq: ${graphqlStringLiteral(parsedQueryDate)} } }`,
      ),
    ];
  } else if (isValidNumber) {
    const numberCols = allAccessibleColumns.filter((col) => {
      const configMatch = numberDataTypes.includes(col.attrDataType?.toLowerCase());
      const typeInfo = columnTypes?.find((ct) => ct.name === col.attrInternalName)?.type;
      return configMatch && (typeInfo === 'Int' || typeInfo === 'float8');
    });

    const stringCols = allAccessibleColumns.filter((col) =>
      stringDataTypes.includes(col.attrDataType?.toLowerCase())
    );

    filters = [
      ...stringCols.map((col) =>
        `{ ${col.attrInternalName}: { _ilike: ${graphqlILikeLiteral(query)} } }`,
      ),
      ...numberCols.map((col) => {
        const typeInfo = columnTypes?.find((ct) => ct.name === col.attrInternalName)?.type;
        const value = typeInfo === 'Int' ? Math.trunc(parsedQueryNumber) : parsedQueryNumber;
        return `{ ${col.attrInternalName}: { _eq: ${value} } }`;
      }),
    ];
  } else {
    const stringCols = allAccessibleColumns.filter((col) =>
      stringDataTypes.includes(col.attrDataType?.toLowerCase())
    );

    filters = stringCols.map(
      (col) => `{ ${col.attrInternalName}: { _ilike: ${graphqlILikeLiteral(query)} } }`,
    );
  }
  const allFilters = [...filters, ...dropdownFilters];
  return allFilters.length ? `_or: [${allFilters.join(', ')}]` : '';
};